import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import {
  Download, Check, ArrowRight, ShieldCheck, Clock4, Sparkles, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { LEITFAEDEN, MAGNET_COURSES } from "@/data/leitfaeden";
import { COURSE_LIST } from "@/data/courses";
import { trackLeadConversion } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";
import { setUserRole } from "@/lib/userRole";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { clock: Clock4, shield: ShieldCheck, sparkles: Sparkles };

const ROLES = [
  ["pflegekraft", "Pflegekraft"],
  ["pdl",         "PDL / Leitung"],
  ["leitung",     "Heim-/GF"],
  ["andere",      "Andere"],
];

export default function LeitfadenLanding() {
  const { slug } = useParams();
  const guide = LEITFAEDEN[slug];
  const isPremium = Boolean(guide?.premium);
  const [form, setForm] = useState({ name: "", email: "", role: "pflegekraft", company: "", position: "", phone: "", bundesland: "", newsletter: false });
  const [state, setState] = useState({ loading: false, success: false, error: null, response: null });

  useEffect(() => {
    if (!guide) return;
    document.title = guide.seoTitle;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = guide.seoDesc;
    window.scrollTo(0, 0);
  }, [guide]);

  if (!guide) return <Navigate to="/leitfaeden" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: null, response: null });
    try {
      const res = await axios.post(`${API}/leadmagnet/${guide.magnetId}`, {
        email: form.email,
        name: form.name,
        role: form.role,
        company: form.company,
        position: form.position,
        phone: form.phone,
        bundesland: form.bundesland,
        consent: true,
        newsletter_opt_in: form.newsletter,
        source: `leadmagnet:${guide.magnetId}`,
        attribution: getAttribution(),
      });
      // DOI: kein direkter Download mehr — User muss erst E-Mail bestätigen
      trackLeadConversion(5, `leadmagnet:${guide.magnetId}`);
      metaLead(`leadmagnet:${guide.magnetId}`);
      setUserRole(form.role);
      setState({ loading: false, success: true, error: null, response: res.data });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = status === 400 && typeof detail === "string"
        ? detail
        : status === 422
          ? "Bitte gültige E-Mail eingeben."
          : "Bitte später erneut versuchen.";
      setState({ loading: false, success: false, error: msg, response: null });
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* HERO + FORM · Editorial Magazine Cover Layout */}
      <section className="relative pt-28 md:pt-36 pb-20 md:pb-28 bg-peach-50 overflow-hidden">
        {/* Editorial crop marks */}
        <div aria-hidden="true" className="hidden lg:block absolute top-24 left-6 w-4 h-4 border-t border-l border-sand-800/25" />
        <div aria-hidden="true" className="hidden lg:block absolute top-24 right-6 w-4 h-4 border-t border-r border-sand-800/25" />
        <div aria-hidden="true" className="hidden lg:block absolute bottom-6 left-6 w-4 h-4 border-b border-l border-sand-800/25" />
        <div aria-hidden="true" className="hidden lg:block absolute bottom-6 right-6 w-4 h-4 border-b border-r border-sand-800/25" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-20 items-center">
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra" data-testid="leitfaden-kicker">
                  {guide.kicker}
                </span>
              </div>

              <h1 className="font-display text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-sand-800 font-normal tracking-tight leading-[1.05]"
                  data-testid="leitfaden-h1">
                {guide.h1a} <br />
                <span className="text-terra italic">{guide.h1b}</span>
              </h1>

              <p className="mt-7 text-sand-700 text-[16px] sm:text-[17px] leading-[1.65] max-w-xl">
                {guide.intro}
              </p>

              {guide.whatsNew && (
                <div
                  data-testid="leitfaden-whats-new"
                  className="mt-7 max-w-xl rounded-2xl border border-terra/25 bg-terra/5 px-5 py-4"
                >
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold text-terra">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-terra animate-pulse" />
                    {guide.whatsNew.version}
                  </div>
                  <div className="mt-2 text-[15px] font-semibold text-sand-800">
                    {guide.whatsNew.title}
                  </div>
                  <ul className="mt-2 space-y-1.5 text-[13.5px] text-sand-700 leading-[1.55]">
                    {guide.whatsNew.bullets.map((b, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="mt-[7px] inline-block h-1 w-1 rounded-full bg-terra/60 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <ul className="mt-8 space-y-3.5 text-[15px] text-sand-700">
                {guide.trust.map((p) => {
                  const Icon = ICONS[p.icon] || Sparkles;
                  return (
                    <li key={p.title} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                        <Icon className="h-4 w-4" strokeWidth={2} />
                      </span>
                      <span>
                        <span className="font-semibold text-sand-800">{p.title}.</span>{" "}
                        <span className="text-sand-600">{p.desc}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-9 flex items-center gap-3 text-[12.5px] text-sand-500">
                <span className="inline-flex h-2 w-2 rounded-full bg-brand-rose animate-pulse" />
                {guide.proof}
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative" data-testid="leitfaden-hero-right">
              {/* Editorial "Digital Edition" Print-Book-Preview — 3D tilted magazine */}
              <div className="mb-10 flex items-start gap-6" data-testid="leitfaden-cover-preview">
                <div
                  className="relative w-[260px] md:w-[300px] lg:w-[340px] aspect-[3/4] shrink-0"
                  style={{ perspective: "1400px" }}
                >
                  <div
                    className="relative w-full h-full rounded-[3px] overflow-hidden shadow-[0_40px_60px_-25px_rgba(82,42,37,0.5)]"
                    style={{
                      transform: "rotateY(-16deg) rotateX(4deg) rotateZ(-1.5deg)",
                      transformOrigin: "left center",
                    }}
                  >
                    <img
                      src={guide.preview}
                      alt={`Cover ${guide.h1b}`}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    {/* Cover spine highlight — creates the "3D book" feel */}
                    <div
                      aria-hidden="true"
                      className="absolute left-0 top-0 h-full w-[6px] bg-gradient-to-r from-black/25 to-transparent"
                    />
                    {/* Grain */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                      }}
                    />
                  </div>
                </div>
                {/* Editorial caption to the right of the cover */}
                <div className="hidden md:block pt-4 min-w-0 flex-1">
                  <div className="font-display italic text-[13px] text-terra">
                    Nº {String(Math.floor(Math.random() * 10) + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-2 text-[10.5px] uppercase tracking-[0.28em] font-semibold text-sand-500">
                    Digital Edition
                  </div>
                  <div className="mt-1 font-display text-[15px] text-sand-800 leading-tight">
                    Vol. 1 · 2026
                  </div>
                  <div className="mt-6 h-px w-10 bg-sand-300" />
                  <div className="mt-3 text-[11px] font-mono text-sand-500">
                    Studio DH · Düsseldorf
                  </div>
                </div>
              </div>

              {/* Editorial form panel — cream inset with terra top border */}
              <div className="relative bg-cream-50 border-t-[3px] border-terra p-7 md:p-9" data-testid="leitfaden-form-card">
                {!state.success ? (
                  <>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-clay-dark mb-3 flex items-center gap-2">
                      {isPremium ? (
                        <>
                          <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                          Premium-Leitfaden · Vorschau anfordern
                        </>
                      ) : (
                        <>Leitfaden anfordern</>
                      )}
                    </div>
                    <h2 className="font-display text-2xl md:text-[1.7rem] text-sand-800 leading-tight">
                      {isPremium
                        ? guide.paidProduct
                          ? `${guide.teaserPages || 4}-Seiten-Vorschau gratis. Vollversion ${guide.paidProduct.price} im Shop.`
                          : `${guide.teaserPages || 4}-Seiten-Vorschau sofort. Vollversion gratis im Campus.`
                        : "Trag deine E-Mail ein – PDF kommt sofort."}
                    </h2>
                    {isPremium && (
                      <p className="mt-3 text-[13.5px] text-sand-600 leading-relaxed">
                        {guide.paidProduct ? (
                          <>Die <b>komplette Vollversion</b> gibt es als Fachbuch-PDF im Shop
                          &#8212; einmal kaufen, dauerhaft deins.</>
                        ) : (
                          <>Die <b>komplette Vollversion</b> liegt im Dickies-Campus &#8212;
                          Zugang für Campus-Mitglieder mit jedem Paket.</>
                        )}
                      </p>
                    )}

                    <form onSubmit={submit} className="mt-7 grid gap-4">
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">
                          Du bist …
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          {ROLES.map(([v, l]) => (
                            <button
                              key={v} type="button"
                              onClick={() => setForm({ ...form, role: v })}
                              data-testid={`leitfaden-role-${v}`}
                              className={`rounded-full px-3 h-10 text-[12.5px] font-semibold border transition-all ${
                                form.role === v
                                  ? "border-terra bg-terra text-white"
                                  : "border-cream-300 text-sand-700 hover:border-clay bg-white"
                              }`}
                            >{l}</button>
                          ))}
                        </div>
                      </div>

                      <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Vor- und Nachname *"
                        data-testid="leitfaden-name"
                        required
                        minLength={2}
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <input
                        required type="email" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="E-Mail-Adresse *"
                        data-testid="leitfaden-email"
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          value={form.company}
                          onChange={(e) => setForm({ ...form, company: e.target.value })}
                          placeholder="Pflegedienst / Klinik (optional)"
                          data-testid="leitfaden-company"
                          className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                        />
                        <input
                          value={form.position}
                          onChange={(e) => setForm({ ...form, position: e.target.value })}
                          placeholder="Position / Funktion (optional)"
                          data-testid="leitfaden-position"
                          className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                        />
                      </div>
                      <input
                        required type="tel" inputMode="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="Telefon (z. B. 0211 12345678) *"
                        data-testid="leitfaden-phone"
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      />
                      <select
                        required value={form.bundesland}
                        onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
                        data-testid="leitfaden-bundesland"
                        className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                      >
                        <option value="">Bundesland * (bitte wählen)</option>
                        <option value="BW">Baden-Württemberg</option>
                        <option value="BY">Bayern</option>
                        <option value="BE">Berlin</option>
                        <option value="BB">Brandenburg</option>
                        <option value="HB">Bremen</option>
                        <option value="HH">Hamburg</option>
                        <option value="HE">Hessen</option>
                        <option value="MV">Mecklenburg-Vorpommern</option>
                        <option value="NI">Niedersachsen</option>
                        <option value="NW">Nordrhein-Westfalen</option>
                        <option value="RP">Rheinland-Pfalz</option>
                        <option value="SL">Saarland</option>
                        <option value="SN">Sachsen</option>
                        <option value="ST">Sachsen-Anhalt</option>
                        <option value="SH">Schleswig-Holstein</option>
                        <option value="TH">Thüringen</option>
                        <option value="AT">Österreich</option>
                        <option value="CH">Schweiz</option>
                        <option value="OTHER">Anderes</option>
                      </select>

                      <label className="flex items-start gap-3 text-[13px] text-sand-600 leading-snug cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={form.newsletter}
                          onChange={(e) => setForm({ ...form, newsletter: e.target.checked })}
                          data-testid="leitfaden-newsletter"
                          className="mt-1 h-4 w-4 accent-terra shrink-0"
                        />
                        <span>
                          <b>Helden-Briefing abonnieren</b> – 1× pro Monat ein PDL-Update zu MD,
                          Abrechnung &amp; Personal. Abmelden jederzeit per Link in jeder Mail.
                        </span>
                      </label>

                      <Button
                        type="submit"
                        disabled={state.loading}
                        data-testid="leitfaden-submit"
                        className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-6 text-[15px] font-semibold mt-1"
                      >
                        {state.loading ? "Wird gesendet …" : (
                          <span className="inline-flex items-center gap-2">
                            <Download className="h-4 w-4" strokeWidth={1.8} />
                            {isPremium ? "Vorschau per E-Mail anfordern" : "Leitfaden per E-Mail anfordern"}
                          </span>
                        )}
                      </Button>

                      {state.error && (
                        <p className="text-[13px] text-terra font-medium" data-testid="leitfaden-error">
                          {state.error}
                        </p>
                      )}
                      <p className="text-[11px] text-sand-500 leading-snug">
                        Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß{" "}
                        <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu.
                        Wir senden dir eine Bestätigungs-Mail — erst nach Klick auf den Link bekommst du den Leitfaden.
                      </p>
                    </form>
                  </>
                ) : (
                  <div data-testid="leitfaden-success" className="text-center py-4">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-rose/30 text-brand-darkred mx-auto">
                      <Check className="h-7 w-7" strokeWidth={2} />
                    </div>
                    <h3 className="mt-5 font-display text-2xl text-sand-800">
                      Fast geschafft!
                    </h3>
                    <p className="mt-3 text-sand-600 text-[14px] leading-relaxed">
                      Wir haben dir gerade eine E-Mail an <strong className="text-sand-800">{form.email}</strong> geschickt.
                      Bitte klicke darin auf den Bestätigungs-Link — danach bekommst du den Leitfaden sofort.
                    </p>
                    <p className="mt-2 text-[12px] text-sand-500">
                      Keine Mail erhalten? Schau bitte auch im Spam-Ordner.
                    </p>

                    {isPremium ? (
                      <div className="mt-7 rounded-2xl bg-brand-darkred text-white p-6 text-left">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brand-rose mb-3 flex items-center gap-2">
                          <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                          {guide.paidProduct ? "Vollversion im Shop" : "Vollversion im Campus"}
                        </div>
                        <p className="text-[14.5px] leading-relaxed"
                           dangerouslySetInnerHTML={{
                             __html: guide.paidProduct
                               ? `${guide.vollversionTeaser || "Die <b>komplette Vollversion</b>"} gibt es als Fachbuch-PDF im Shop — ${guide.paidProduct.price}, einmal kaufen, dauerhaft deins.`
                               : `${guide.vollversionTeaser || "Die <b>komplette Vollversion</b>"} liegt im Dickies-Campus — Zugang für Mitglieder.`,
                           }} />
                        {guide.paidProduct ? (
                          <Link to={guide.paidProduct.url}
                                data-testid="leitfaden-shop-cta"
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-brand-darkred px-5 h-11 text-[14px] font-semibold hover:bg-brand-rose/30 transition">
                            Vollversion kaufen · {guide.paidProduct.price} <ArrowRight className="h-4 w-4" />
                          </Link>
                        ) : (
                          <>
                            <Link to="/preise"
                                  data-testid="leitfaden-register-cta"
                                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-brand-darkred px-5 h-11 text-[14px] font-semibold hover:bg-brand-rose/30 transition">
                              Mitglied werden <ArrowRight className="h-4 w-4" />
                            </Link>
                            <p className="mt-4 text-[11.5px] text-brand-rose/80">
                              Schon Mitglied?{" "}
                              <Link to="/campus/login" className="underline text-white font-semibold">
                                Hier einloggen
                              </Link>
                              {" "}und direkt die Vollversion laden.
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="mt-7 rounded-2xl bg-white border border-cream-300 p-5 text-left">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-terra mb-2">
                          Bonus für dich
                        </div>
                        <p className="text-[14px] text-sand-700 leading-relaxed">
                          {guide.bridge} <br />
                          <Link to="/campus"
                                data-testid="leitfaden-campus-cta"
                                className="mt-3 inline-flex items-center gap-1.5 text-terra font-semibold underline">
                            Dickies-Campus ansehen <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-brand-rose py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <blockquote className="font-display italic text-2xl md:text-[1.85rem] text-brand-darkred leading-[1.4] text-center">
              {guide.quote}
            </blockquote>
            <p className="mt-7 text-center text-white text-[13px] uppercase tracking-[0.22em] font-semibold">
              {guide.quoteAuthor}
            </p>
          </Reveal>
        </div>
      </section>

      {/* CAMPUS BRIDGE */}
      <section className="bg-white py-28 md:py-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-terra" />
                  <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                    Das ist erst der Anfang
                  </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] text-sand-800 font-normal tracking-tight leading-[1.1]">
                  Im Dickies-Campus wartet die <span className="text-terra">ganze Bibliothek</span>.
                </h2>
                <p className="mt-6 text-sand-700 text-[15px] leading-[1.7] max-w-xl">
                  {guide.bridge} <b>Inkludiert in jeder Weiterbildung bei Dickies-Campus.</b>
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/campus" data-testid="leitfaden-bridge-campus">
                    <Button className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-7 text-[15px] font-semibold">
                      <span className="inline-flex items-center gap-2">
                        Dickies-Campus ansehen <ArrowRight className="h-4 w-4" />
                      </span>
                    </Button>
                  </Link>
                  <Link to="/leitfaeden" data-testid="leitfaden-bridge-bibliothek">
                    <Button variant="outline"
                            className="rounded-full border-clay text-sand-800 hover:bg-cream-100 h-12 px-7 text-[15px] font-semibold">
                      Alle Leitfäden ansehen
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {guide.stats.map(([k, v]) => (
                  <div key={k} className="rounded-3xl bg-cream-50 border border-cream-300 p-6 shadow-soft">
                    <div className="font-display text-[2rem] text-terra leading-none">{v}</div>
                    <div className="mt-2 text-[13px] uppercase tracking-[0.2em] font-bold text-clay-dark">
                      {k}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ====================================================================
          PASSEND DAZU · Schulungs-Empfehlungen
          Treibt PDF-Downloader Richtung Weiterbildungs-Buchung (Hauptgeschäft)
          ==================================================================== */}
      {(() => {
        const courseIds = MAGNET_COURSES[slug] || MAGNET_COURSES._default || [];
        const courses = courseIds
          .map((id) => COURSE_LIST.find((c) => c.id === id))
          .filter(Boolean)
          .slice(0, 3);
        if (courses.length === 0) return null;
        return (
          <section className="bg-cream-50 py-20 border-t border-cream-200" data-testid="related-courses-section">
            <div className="mx-auto max-w-6xl px-6">
              <Reveal>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-terra" />
                  <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                    Passend zu diesem Leitfaden
                  </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] text-sand-800 font-normal tracking-tight leading-[1.1] max-w-3xl">
                  Diese <span className="text-terra">Weiterbildungen</span> machen das Wissen aus diesem Leitfaden zur <span className="text-terra">offiziellen Qualifikation</span>.
                </h2>
                <p className="mt-6 text-sand-700 text-[15px] leading-[1.7] max-w-2xl">
                  Wissen allein reicht nicht – für eine MD-konforme Abrechnung und rechtssichere Delegation brauchst du das passende Zertifikat. Hier die drei Schulungen, die dieses Leitfaden-Thema in echte Berufsqualifikation übersetzen.
                </p>

                <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="related-courses-grid">
                  {courses.map((c) => (
                    <Link
                      key={c.id}
                      to={`/kurse/${c.id}`}
                      data-testid={`related-course-${c.id}`}
                      className="group block rounded-3xl bg-white border border-cream-300 p-7 shadow-soft hover:shadow-lg hover:border-terra/40 transition-all"
                    >
                      <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">
                        {c.category === "fuehrung" ? "Führung" : c.category === "erfahren" ? "Aufbau" : c.category === "einstieg" ? "Einstieg" : "Pflicht"}
                      </div>
                      <h3 className="mt-3 font-display text-2xl text-sand-800 leading-tight group-hover:text-terra transition-colors">
                        {c.title}
                      </h3>
                      <p className="mt-2 text-sand-600 text-sm leading-relaxed">{c.subtitle}</p>
                      <div className="mt-5 flex items-center justify-between border-t border-cream-200 pt-4">
                        <div>
                          <div className="text-2xl font-display text-terra leading-none">{c.price}</div>
                          <div className="mt-1 text-[11px] uppercase tracking-[0.15em] text-clay-dark font-semibold">
                            {c.duration}
                          </div>
                        </div>
                        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra group-hover:gap-2.5 transition-all">
                          Details <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                      {c.funding && (
                        <div className="mt-3 text-[12px] text-clay-dark italic">{c.funding}</div>
                      )}
                    </Link>
                  ))}
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link to="/kurse" data-testid="related-courses-all">
                    <Button variant="outline" className="rounded-full border-clay text-sand-800 hover:bg-cream-100 h-12 px-7 text-[15px] font-semibold">
                      Alle Weiterbildungen ansehen <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/kontakt" data-testid="related-courses-beratung">
                    <Button className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-7 text-[15px] font-semibold">
                      30-Min-Beratung gratis
                    </Button>
                  </Link>
                  <span className="text-sm text-sand-600">Bildungsgutschein möglich · Förderfähig nach AZAV</span>
                </div>
              </Reveal>
            </div>
          </section>
        );
      })()}

      <Footer />
    </div>
  );
}
